import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grow,
  Paper,
  PaperProps,
  Popper,
  PopperProps,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { alpha, lighten, styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface MuiAutocompleteMultiSelectProps<T> {
  options: readonly T[];
  value: readonly T[];
  onChange: (next: T[]) => void;
  label?: string;
  multiSelect?: boolean;
  /** Optional popup title, shown only in multiselect mode. */
  header?: string;
  disabled?: boolean;
  className?: string;
  size?: "small" | "medium";
  rowHeight?: "default" | "condensed";
  minDropdownWidth?: number | string;
  dropdownAlign?: "left" | "right";
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  loading?: boolean;
  showLoadingIndicator?: boolean;
  loadingText?: string;
  disableSearch?: boolean;
  searchThreshold?: number;
  renderOptionContent?: (item: T, state: { selected: boolean }) => React.ReactNode;
  sx?: SxProps<Theme>;
  getOptionLabel: (item: T) => string;
  getOptionKey: (item: T) => string | number;
  placeholder?: string;
  emptyText?: string;
  getOptionSecondary?: (item: T) => React.ReactNode;
  currentUserRole?: string;
  allowDemoForRoles?: string[];
  isDemo?: (item: T) => boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}

interface MuiAutocompleteMultiSelectPaperContextValue {
  multiSelect?: boolean;
  header?: string;
  visibleOptionCount?: number;
  selectedCount?: number;
  hiddenSelectedCount?: number;
  hasScrolledPastSelectedSection?: boolean;
  onSelectAll?: () => void;
  onClearAll?: () => void;
  onDone?: () => void;
  onScrollSelectedToTop?: () => void;
  paperElementRef?: React.Ref<HTMLDivElement>;
}

const MuiAutocompleteMultiSelectPaperContext =
  React.createContext<MuiAutocompleteMultiSelectPaperContextValue | null>(null);

const MuiAutocompleteMultiSelectPopper = React.forwardRef<HTMLDivElement, PopperProps>(
  function MuiAutocompleteMultiSelectPopper({ children, ...props }, ref) {
    return (
      <Popper {...props} ref={ref} transition>
        {({ TransitionProps, placement }) => (
          <Grow {...TransitionProps} timeout="auto">
            <Box
              sx={{
                transformOrigin: `${placement.startsWith("top") ? "bottom" : "top"} ${placement.endsWith("end") ? "right" : "left"}`,
              }}
            >
              {typeof children === "function" ? children({ TransitionProps, placement }) : children}
            </Box>
          </Grow>
        )}
      </Popper>
    );
  },
);

const SelectedJumpButton = styled("button")(({ theme }) => ({
  position: "absolute",
  top: 8,
  insetInlineEnd: 20,
  zIndex: 1,
  minWidth: 0,
  height: 24,
  paddingInline: theme.spacing(0.75),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.25),
  borderRadius: 999,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
  outline: 0,
  cursor: "pointer",
  fontSize: "0.75rem",
  fontWeight: 600,
  lineHeight: 1,
  backgroundColor: lighten(theme.palette.primary.main, 0.92),
  color: theme.palette.primary.main,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
  "&:hover": {
    backgroundColor: lighten(theme.palette.primary.main, 0.88),
    borderColor: theme.palette.primary.main,
  },
  "&:focus-visible": {
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (!ref) return;

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  ref.current = value;
};

const MuiAutocompleteMultiSelectPaper = React.forwardRef<HTMLDivElement, PaperProps>(
  function MuiAutocompleteMultiSelectPaper({ children, ...paperProps }, ref) {
    const { t } = useTranslation();
    const context = React.useContext(MuiAutocompleteMultiSelectPaperContext);
    const multiSelect = context?.multiSelect ?? false;
    const visibleOptionCount = context?.visibleOptionCount ?? 0;
    const selectedCount = context?.selectedCount ?? 0;
    const hiddenSelectedCount = context?.hiddenSelectedCount ?? 0;
    const hasScrolledPastSelectedSection = context?.hasScrolledPastSelectedSection ?? false;
    const onSelectAll = context?.onSelectAll;
    const onClearAll = context?.onClearAll;
    const onDone = context?.onDone;
    const onScrollSelectedToTop = context?.onScrollSelectedToTop;
    const paperElementRef = context?.paperElementRef;
    const selectedJumpButton =
      hiddenSelectedCount > 0 && hasScrolledPastSelectedSection ? (
        <SelectedJumpButton
          type="button"
          onClick={onScrollSelectedToTop}
          aria-label={t("common.multiSelect.actions.scrollSelectedToTop", {
            count: hiddenSelectedCount,
          })}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: 10 }} />
          {t("common.multiSelect.actions.viewSelected")}
        </SelectedJumpButton>
      ) : null;

    return (
      <Paper
        {...paperProps}
        ref={(node) => {
          assignRef(ref, node);
          assignRef(paperElementRef, node);
        }}
        sx={{
          mt: 1,
          overflow: "hidden",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: 8,
          ...paperProps.sx,
        }}
      >
        {multiSelect && context?.header && (
          <Typography
            variant="subtitle2"
            sx={{
              px: 2,
              py: 1,
              fontSize: "1rem",
              fontWeight: "medium",
              borderBottom: "1px solid",
              borderColor: "divider",
              textAlign: "start",
            }}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            {context.header}
          </Typography>
        )}
        {multiSelect && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={onSelectAll}
                disabled={visibleOptionCount === 0}
              >
                {t("common.multiSelect.text.selectAllCount", { count: visibleOptionCount })}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={onClearAll}
                disabled={selectedCount === 0}
              >
                {t("common.clear")}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t("common.multiSelect.text.selectedCount", { count: selectedCount })}
            </Typography>
          </Box>
        )}

        <Box sx={{ position: "relative" }}>
          {selectedJumpButton}
          {children}
        </Box>

        {multiSelect && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 1.5,
              py: 1,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            <Button size="small" variant="outlined" onClick={onDone}>
              {t("common.done")}
            </Button>
          </Box>
        )}
      </Paper>
    );
  },
);

const MuiAutocompleteMultiSelect = <T,>({
  options,
  value,
  onChange,
  label,
  multiSelect = false,
  header,
  disabled = false,
  className,
  size = "medium",
  rowHeight,
  minDropdownWidth,
  dropdownAlign = "left",
  required = false,
  error = false,
  helperText,
  loading = false,
  showLoadingIndicator = true,
  loadingText,
  disableSearch = false,
  searchThreshold = 0,
  renderOptionContent,
  sx,
  getOptionLabel,
  getOptionKey,
  placeholder,
  emptyText,
  getOptionSecondary,
  currentUserRole,
  allowDemoForRoles,
  isDemo,
  inputRef: forwardedInputRef,
}: MuiAutocompleteMultiSelectProps<T>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<T[] | null>(null);
  const [hiddenSelectedCount, setHiddenSelectedCount] = useState(0);
  const [hasScrolledPastSelectedSection, setHasScrolledPastSelectedSection] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxRef = useRef<HTMLElement | null>(null);
  const skipNextCloseRef = useRef(false);
  const blurOnPopupCloseRef = useRef(false);
  const pendingListboxScrollTopRef = useRef<number | null>(null);

  const keyFor = useCallback((item: T) => String(getOptionKey(item)), [getOptionKey]);

  const demoAllowedRoles = useMemo(
    () => (allowDemoForRoles && allowDemoForRoles.length > 0 ? allowDemoForRoles : ["CRAVE_ADMIN"]),
    [allowDemoForRoles],
  );

  const shouldHideDemo = useMemo(() => {
    if (!isDemo) return false;
    if (!currentUserRole) return true;
    return !demoAllowedRoles.includes(currentUserRole);
  }, [currentUserRole, demoAllowedRoles, isDemo]);

  const filterOutDemo = useCallback(
    (items: readonly T[]) => {
      if (!shouldHideDemo || !isDemo) return Array.from(items);
      return items.filter((item) => !isDemo(item));
    },
    [isDemo, shouldHideDemo],
  );

  const availableOptions = useMemo(() => filterOutDemo(options), [filterOutDemo, options]);
  const committedValue = useMemo(() => filterOutDemo(value), [filterOutDemo, value]);
  const workingValue = open ? (pending ?? committedValue) : committedValue;

  const visibleOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return availableOptions;

    return availableOptions.filter((option) =>
      getOptionLabel(option).toLowerCase().includes(query),
    );
  }, [availableOptions, getOptionLabel, search]);

  const descendingOptionSort = useCallback(
    (left: T, right: T) => {
      const leftKey = Number(keyFor(left));
      const rightKey = Number(keyFor(right));
      const leftHasNumericKey = Number.isFinite(leftKey);
      const rightHasNumericKey = Number.isFinite(rightKey);

      if (leftHasNumericKey && rightHasNumericKey && leftKey !== rightKey) {
        return rightKey - leftKey;
      }

      return getOptionLabel(right).localeCompare(getOptionLabel(left), undefined, {
        sensitivity: "base",
      });
    },
    [getOptionLabel, keyFor],
  );

  const areSameSelection = useCallback(
    (left: readonly T[], right: readonly T[]) => {
      const leftKeys = left.map(keyFor).sort();
      const rightKeys = right.map(keyFor).sort();

      return (
        leftKeys.length === rightKeys.length &&
        leftKeys.every((entry, index) => entry === rightKeys[index])
      );
    },
    [keyFor],
  );

  const getSelectionSignature = useCallback(
    (items: readonly T[]) => items.map(keyFor).sort().join("|"),
    [keyFor],
  );

  const committedSelectionSignature = useMemo(
    () => getSelectionSignature(committedValue),
    [committedValue, getSelectionSignature],
  );
  const previousCommittedSelectionSignatureRef = useRef(committedSelectionSignature);

  const commitValue = useCallback(
    (nextOverride?: T[]) => {
      const next = filterOutDemo(nextOverride ?? pending ?? committedValue);
      setPending(null);
      setSearch("");

      if (!areSameSelection(committedValue, next)) {
        onChange(next);
      }
    },
    [areSameSelection, committedValue, filterOutDemo, onChange, pending],
  );

  const blurActiveElement = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const closeAndCommit = useCallback(
    (nextOverride?: T[]) => {
      skipNextCloseRef.current = true;
      blurActiveElement();
      commitValue(nextOverride);
      setOpen(false);
    },
    [blurActiveElement, commitValue],
  );

  const openDropdown = useCallback(() => {
    if (disabled) return;

    setPending(committedValue);
    setSearch("");
    setOpen(true);
  }, [committedValue, disabled]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const handleClosedControlMouseDown = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const popupIndicatorTarget = target.closest(".MuiAutocomplete-popupIndicator");
      if (open && popupIndicatorTarget) {
        blurOnPopupCloseRef.current = true;
        return;
      }

      if (disabled || open) return;

      if (popupIndicatorTarget || target.closest(".MuiAutocomplete-clearIndicator")) {
        return;
      }

      event.preventDefault();
      openDropdown();
      focusInput();
    },
    [disabled, focusInput, open, openDropdown],
  );

  const handleLabelMouseDown = useCallback(
    (event: React.MouseEvent<HTMLLabelElement>) => {
      if (disabled || open) return;

      event.preventDefault();
      openDropdown();
      focusInput();
    },
    [disabled, focusInput, open, openDropdown],
  );

  useEffect(() => {
    if (!open) {
      previousCommittedSelectionSignatureRef.current = committedSelectionSignature;
      return;
    }

    if (previousCommittedSelectionSignatureRef.current === committedSelectionSignature) {
      return;
    }

    previousCommittedSelectionSignatureRef.current = committedSelectionSignature;
    setPending(committedValue);
  }, [committedSelectionSignature, committedValue, open]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      const insideRoot = rootRef.current?.contains(target) ?? false;
      const insidePaper = paperRef.current?.contains(target) ?? false;

      if (!insideRoot && !insidePaper) {
        closeAndCommit();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, {
      passive: true,
    });

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown as EventListener);
    };
  }, [closeAndCommit, open]);

  const buttonLabel = () => {
    const total = availableOptions.length;
    const selected = workingValue.length;

    if (!multiSelect) {
      return selected > 0 ? getOptionLabel(workingValue[0]) : "";
    }

    if (total > 0 && selected === total) {
      return t("common.multiSelect.text.allSelected");
    }
    if (selected === 0) return "";

    const firstTwo = workingValue.slice(0, 2).map(getOptionLabel);
    const extra = selected - firstTwo.length;

    return firstTwo.join(", ") + (extra > 0 ? ` +${extra}` : "");
  };

  const updatePendingValue = useCallback((next: T[]) => {
    setPending(next);
  }, []);

  const selectAll = useCallback(() => {
    updatePendingValue(visibleOptions);
  }, [updatePendingValue, visibleOptions]);

  const clearAll = useCallback(() => {
    updatePendingValue([]);
  }, [updatePendingValue]);

  const selectedKeys = useMemo(() => new Set(workingValue.map(keyFor)), [keyFor, workingValue]);

  const visibleSelectedOptions = useMemo(
    () => availableOptions.filter((option) => selectedKeys.has(keyFor(option))),
    [availableOptions, keyFor, selectedKeys],
  );

  const matchingUnselectedOptions = useMemo(
    () => visibleOptions.filter((option) => !selectedKeys.has(keyFor(option))),
    [keyFor, selectedKeys, visibleOptions],
  );

  const orderedVisibleOptions = useMemo(() => {
    if (!multiSelect) {
      return visibleOptions;
    }

    const sortedSelectedOptions = [...visibleSelectedOptions].sort(descendingOptionSort);
    const sortedUnselectedOptions = [...matchingUnselectedOptions].sort(descendingOptionSort);

    return [...sortedSelectedOptions, ...sortedUnselectedOptions];
  }, [
    descendingOptionSort,
    visibleOptions,
    matchingUnselectedOptions,
    multiSelect,
    visibleSelectedOptions,
  ]);

  const showSelectedDivider =
    multiSelect && visibleSelectedOptions.length > 0 && matchingUnselectedOptions.length > 0;
  const groupByOption = useCallback(
    (option: T) => (selectedKeys.has(keyFor(option)) ? "selected" : "unselected"),
    [keyFor, selectedKeys],
  );

  const effectiveRowHeight = rowHeight ?? (multiSelect ? "condensed" : "default");
  const controlHeight = size === "small" ? 40 : 56;
  const optionHeight = effectiveRowHeight === "condensed" ? controlHeight / 2 : controlHeight;
  const count = workingValue.length;
  const searchEnabled = !disableSearch && availableOptions.length >= searchThreshold;
  const hasEndCountBadge = multiSelect && count > 0;
  const endAdornmentPadding = hasEndCountBadge ? 10 : 6;
  const endAdornmentPaddingPx = `${endAdornmentPadding * 8}px`;
  const labelRightInsetPx = `${endAdornmentPadding * 8 + 8}px`;
  const updateHiddenSelectedState = useCallback(
    (scrollTop: number) => {
      if (!multiSelect || visibleSelectedOptions.length === 0) {
        setHiddenSelectedCount(0);
        setHasScrolledPastSelectedSection(false);
        return;
      }

      const selectedSectionHeight = visibleSelectedOptions.length * optionHeight;
      const nextCount = Math.min(
        visibleSelectedOptions.length,
        Math.max(0, Math.floor(scrollTop / optionHeight)),
      );
      const nextHasScrolledPastSelectedSection = scrollTop >= selectedSectionHeight;

      setHiddenSelectedCount((current) => (current === nextCount ? current : nextCount));
      setHasScrolledPastSelectedSection((current) =>
        current === nextHasScrolledPastSelectedSection
          ? current
          : nextHasScrolledPastSelectedSection,
      );
    },
    [multiSelect, optionHeight, visibleSelectedOptions.length],
  );

  const setListboxElement = useCallback(
    (node: HTMLElement | null) => {
      listboxRef.current = node;
      updateHiddenSelectedState(node?.scrollTop ?? 0);
    },
    [updateHiddenSelectedState],
  );

  useEffect(() => {
    if (!open || !multiSelect) {
      listboxRef.current = null;
      pendingListboxScrollTopRef.current = null;
      setHiddenSelectedCount(0);
      setHasScrolledPastSelectedSection(false);
      return;
    }

    const listbox = listboxRef.current;
    if (!listbox) {
      setHiddenSelectedCount(0);
      setHasScrolledPastSelectedSection(false);
      return;
    }

    if (pendingListboxScrollTopRef.current !== null) {
      listbox.scrollTop = pendingListboxScrollTopRef.current;
      pendingListboxScrollTopRef.current = null;
    }

    updateHiddenSelectedState(listbox.scrollTop);
  }, [multiSelect, open, orderedVisibleOptions, selectedKeys, updateHiddenSelectedState]);
  const resolvedLabel = label ?? t("common.multiSelect.fields.select");
  const resolvedLoadingText = loadingText ?? t("common.loading");
  const resolvedPlaceholder = placeholder ?? t("common.multiSelect.placeholders.search");
  const resolvedEmptyText = emptyText ?? t("common.multiSelect.empty.noOptions");
  const paperContextValue = useMemo<MuiAutocompleteMultiSelectPaperContextValue>(
    () => ({
      multiSelect,
      header,
      visibleOptionCount: visibleOptions.length,
      selectedCount: count,
      hiddenSelectedCount,
      hasScrolledPastSelectedSection,
      onSelectAll: selectAll,
      onClearAll: clearAll,
      onDone: () => closeAndCommit(),
      onScrollSelectedToTop: () => {
        const listbox = listboxRef.current;
        if (!listbox) return;

        if (typeof listbox.scrollTo === "function") {
          listbox.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }

        listbox.scrollTop = 0;
      },
      paperElementRef: paperRef,
    }),
    [
      clearAll,
      closeAndCommit,
      count,
      hasScrolledPastSelectedSection,
      hiddenSelectedCount,
      header,
      multiSelect,
      selectAll,
      visibleOptions.length,
    ],
  );

  return (
    <MuiAutocompleteMultiSelectPaperContext.Provider value={paperContextValue}>
      <Box ref={rootRef} className={className}>
        <Autocomplete
          multiple
          disableCloseOnSelect={multiSelect}
          disablePortal={false}
          disableClearable
          disabled={disabled}
          loading={loading}
          loadingText={resolvedLoadingText}
          open={open}
          openOnFocus
          options={orderedVisibleOptions}
          value={workingValue}
          inputValue={open && searchEnabled ? search : ""}
          noOptionsText={resolvedEmptyText}
          filterOptions={(entries) => entries}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, selected) => keyFor(option) === keyFor(selected)}
          groupBy={multiSelect ? groupByOption : undefined}
          sx={[
            {
              "& .MuiAutocomplete-inputRoot": {
                paddingRight: "8px !important",
              },
              "& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
                paddingRight: "8px !important",
              },
              "& .MuiAutocomplete-listbox": {
                position: "relative",
                paddingTop: hiddenSelectedCount > 0 ? 4 : 0,
              },
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
          onOpen={openDropdown}
          onClose={() => {
            if (skipNextCloseRef.current) {
              skipNextCloseRef.current = false;
              return;
            }

            const shouldBlurOnPopupClose = blurOnPopupCloseRef.current;
            blurOnPopupCloseRef.current = false;

            commitValue();
            setOpen(false);

            if (shouldBlurOnPopupClose) {
              requestAnimationFrame(() => {
                inputRef.current?.blur();
                blurActiveElement();
              });
              return;
            }

            blurActiveElement();
          }}
          onInputChange={(_event, nextValue, reason) => {
            if (searchEnabled && reason === "input") {
              setSearch(nextValue);
            }
          }}
          onChange={(_event, nextValue) => {
            const normalizedNext = filterOutDemo(nextValue);

            if (!multiSelect) {
              const nextSingle =
                normalizedNext.length > 0 ? [normalizedNext[normalizedNext.length - 1]] : [];
              closeAndCommit(nextSingle);
              return;
            }

            pendingListboxScrollTopRef.current = listboxRef.current?.scrollTop ?? null;
            updatePendingValue(normalizedNext);
          }}
          slots={{
            paper: MuiAutocompleteMultiSelectPaper,
            popper: MuiAutocompleteMultiSelectPopper,
          }}
          renderGroup={(params) => (
            <Box component="li" key={params.key} sx={{ p: 0, listStyle: "none" }}>
              {params.group === "unselected" && showSelectedDivider ? (
                <Box
                  component="div"
                  role="separator"
                  sx={{
                    mx: 0,
                    my: 0,
                    height: 6,
                    bgcolor: "divider",
                  }}
                />
              ) : null}
              <Box component="ul" sx={{ m: 0, p: 0 }}>
                {params.children}
              </Box>
            </Box>
          )}
          renderOption={(props, option, state) => (
            <Box
              component="li"
              {...props}
              key={keyFor(option)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: getOptionSecondary ? 0.25 : 0,
                height: optionHeight,
                justifyContent: "center",
                boxSizing: "border-box",
                px: 2,
                py: 0,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: state.selected ? "primary.50" : "background.paper",
                color: state.selected ? "primary.main" : "text.primary",
                "&:last-of-type": {
                  borderBottom: 0,
                },
              }}
            >
              {renderOptionContent ? (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  {renderOptionContent(option, { selected: state.selected })}
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2" noWrap sx={{ width: "100%", fontWeight: 500 }}>
                    {getOptionLabel(option)}
                  </Typography>
                  {getOptionSecondary ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ width: "100%" }}
                    >
                      {getOptionSecondary(option)}
                    </Typography>
                  ) : null}
                </Box>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={(node) => {
                inputRef.current = node;
                assignRef(forwardedInputRef, node);

                const paramsInputRef = params.inputProps.ref as
                  React.Ref<HTMLInputElement> | undefined;
                assignRef(paramsInputRef, node);
              }}
              label={resolvedLabel}
              size={size}
              required={false}
              error={error}
              helperText={helperText}
              placeholder={open && searchEnabled ? resolvedPlaceholder : undefined}
              inputProps={{
                ...params.inputProps,
                required: required && count === 0,
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                shrink: open || count > 0,
                required,
                onMouseDown: (event) => {
                  params.InputLabelProps?.onMouseDown?.(event);
                  handleLabelMouseDown(event);
                },
              }}
              InputProps={{
                ...params.InputProps,
                onMouseDown: (event) => {
                  params.InputProps.onMouseDown?.(event);
                  handleClosedControlMouseDown(event);
                },
                readOnly: !open || !searchEnabled,
                startAdornment:
                  !open && count > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flex: "1 1 auto",
                        maxWidth: `calc(100% - ${endAdornmentPaddingPx})`,
                        minWidth: 0,
                        overflow: "hidden",
                        pl: 0.5,
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          flex: "0 1 auto",
                          minWidth: 0,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: disabled
                            ? "text.disabled"
                            : count === 0
                              ? "text.secondary"
                              : "text.primary",
                          fontWeight: 700,
                        }}
                      >
                        {buttonLabel()}
                      </Typography>
                    </Box>
                  ) : undefined,
                endAdornment: (
                  <Box
                    sx={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                      justifyContent: "flex-end",
                      maxWidth: "calc(100% - 28px)",
                    }}
                  >
                    {loading && showLoadingIndicator ? <CircularProgress size={18} /> : null}
                    {multiSelect && count > 0 ? (
                      <Box
                        sx={{
                          minWidth: 24,
                          height: 24,
                          px: 0.5,
                          borderRadius: 999,
                          bgcolor: "rgba(26, 117, 255, 0.08)",
                          color: "primary.dark",
                          border: "1px solid rgba(26, 117, 255, 0.16)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {count}
                      </Box>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Box>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: size === "small" ? 40 : 56,
                  pr: endAdornmentPadding,
                  flexWrap: "nowrap",
                  alignItems: "center",
                  overflow: "hidden",
                },
                "& .MuiInputLabel-root": {
                  maxWidth: `calc(100% - ${labelRightInsetPx} - 14px)`,
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 1,
                },
                "& .MuiAutocomplete-inputRoot": {
                  flexWrap: "nowrap !important",
                  alignItems: "center",
                  pr: `${endAdornmentPaddingPx} !important`,
                },
                "& .MuiAutocomplete-input": {
                  opacity: open ? 1 : 0,
                  flexGrow: open ? 1 : 0,
                  width: open ? "100% !important" : "1px !important",
                  minWidth: open ? 30 : "1px !important",
                  alignSelf: "center",
                  fontSize: "max(1rem, 16px)",
                  ...(open
                    ? {}
                    : {
                        p: "0 !important",
                        caretColor: "transparent",
                      }),
                  textAlign: "start",
                },
                "& .MuiAutocomplete-endAdornment": {
                  position: "static",
                  transform: "none",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  margin: 0,
                },
                "& .MuiAutocomplete-popupIndicator": {
                  flexShrink: 0,
                  ml: 0,
                },
                "& .MuiAutocomplete-clearIndicator": {
                  flexShrink: 0,
                },
              }}
            />
          )}
          slotProps={{
            listbox: {
              ref: setListboxElement,
              onScroll: (event: React.UIEvent<HTMLElement>) => {
                updateHiddenSelectedState(event.currentTarget.scrollTop);
              },
              sx: {
                maxHeight: 320,
                p: 0,
                "& .MuiAutocomplete-option": {
                  minHeight: `${optionHeight}px`,
                  height: `${optionHeight}px`,
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                  boxSizing: "border-box",
                },
              },
            },
            popper: {
              placement: dropdownAlign === "right" ? "bottom-end" : "bottom-start",
              sx: {
                zIndex: (theme) => theme.zIndex.modal + 1,
                minWidth: minDropdownWidth,
              },
            },
          }}
        />
      </Box>
    </MuiAutocompleteMultiSelectPaperContext.Provider>
  );
};

export default MuiAutocompleteMultiSelect;
